import {
  Button,
  OutlinedSelect,
  OutlinedSelectOptionType,
} from '@octano/global-ui';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Col, Row } from 'reactstrap';

interface Props {
  periods: { id: number; name: string }[];
  processTypes: { id: number; name: string }[];
  selectedPeriodId: number | null;
  selectedProcessTypeId: number | null;
  onPeriodChange: (id: number | null) => void;
  onProcessTypeChange: (id: number | null) => void;
  onSearch: () => void;
  onClear: () => void;
  onCreate: () => void;
}

export default function AdmissionProcessesFilter({
  periods,
  processTypes,
  selectedPeriodId,
  selectedProcessTypeId,
  onPeriodChange,
  onProcessTypeChange,
  onSearch,
  onClear,
  onCreate,
}: Props) {
  const { t } = useTranslation('academic-offer/admission-processes');

  const periodOptions = useMemo<OutlinedSelectOptionType[]>(
    () => periods.map((p) => ({ value: p.id, label: p.name })),
    [periods],
  );

  const processTypeOptions = useMemo<OutlinedSelectOptionType[]>(
    () => processTypes.map((pt) => ({ value: pt.id, label: pt.name })),
    [processTypes],
  );

  const selectedPeriodOption = useMemo(
    () =>
      periodOptions.find((o) => o.value === selectedPeriodId) || (null as any),
    [periodOptions, selectedPeriodId],
  );

  const selectedProcessTypeOption = useMemo(
    () =>
      processTypeOptions.find((o) => o.value === selectedProcessTypeId) ||
      (null as any),
    [processTypeOptions, selectedProcessTypeId],
  );

  return (
    <Row className="mb-2">
      <Col md={4}>
        <OutlinedSelect
          name="period"
          label={t('filters.period')}
          options={periodOptions}
          value={selectedPeriodOption}
          onChange={(val: any) =>
            onPeriodChange(val ? Number(val.value) : null)
          }
        />
      </Col>
      <Col md={4}>
        <OutlinedSelect
          name="processType"
          label={t('filters.processType')}
          options={processTypeOptions}
          value={selectedProcessTypeOption}
          onChange={(val: any) =>
            onProcessTypeChange(val ? Number(val.value) : null)
          }
          isClearable
        />
      </Col>

      <Col
        md={{ size: 3, offset: 1 }}
        className="d-flex align-items-center justify-content-end mt-2"
      >
        <Button
          text={t('filters.search')}
          onClick={onSearch}
          disabled={!selectedPeriodId}
          className="mr-2 font-weight-bold flex-grow-1"
          size="md"
        />
        <Button
          text={t('filters.clear')}
          outlined={true}
          className="font-weight-bold flex-grow-1"
          onClick={onClear}
          size="md"
        />
      </Col>
      <Col md={{ size: 3, offset: 9 }} className="mt-2">
        <Button
          text={t('filters.createProcess')}
          onClick={onCreate}
          size="md"
          className="font-weight-bold"
          fullwidth
        />
      </Col>
    </Row>
  );
}
